import { fuzzy } from 'fast-fuzzy';
import { NextRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';

import {
  convertEquipmentsToShipEditTree,
  convertShipTreeEditToTreeView,
} from '../components/tree/converters';
import { CurrencySymbolType, CurrencyType } from '../types/CommonTypes';
import { TreeShipViewItemType } from '../types/EquipmentTypes';
import {
  OfferLumpsumType,
  OfferManHoursType,
  OfferStatusType,
  OfferUnderwaterType,
  TimeUnitType,
} from '../types/OfferTypes';
import { PortType } from '../types/PortType';
import {
  ServiceRequestStatusType,
  ServiceRequestType,
} from '../types/ServiceRequestTypes';
import { ShipEquipmentType, ShipType } from '../types/ShipTypes';
import {
  TransactionStatusType,
  TransactionType,
  TransactionTypeType,
} from '../types/TransactionTypes';
import { TroubleshootingOfferType } from '../types/TroubleshootingRequestTypes';
import { CertificationsType, UserType, UserTypeType } from '../types/UserTypes';
import countries from './countries';
import { fromEnum, toEnum } from './enums';
import equipmentStructure from './equipmentStructure';
import request from './request';
import upload from './upload';

export const months = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export const supplierBalanceTransactionStatuses = [
  TransactionStatusType.INVOICE_SENT,
  TransactionStatusType.PENDING,
];

export const toTitleCase = (str: string): string => {
  return str.replace(/\w\S*/g, (txt) => {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
};

export const formatExpirationDate = (
  expiration: number
): {
  expirationString: string;
  days: number;
  hours: number;
  minutes: number;
} => {
  let expirationString = '';
  const expirationDate = new Date(expiration);
  const diff: number = expirationDate.getTime() - new Date().getTime();
  let days;
  let hours;
  let minutes;

  if (diff < 0) {
    expirationString = `${expirationDate.getDate()} ${
      months[expirationDate.getMonth()]
    } ${expirationDate.getFullYear()}`;
  } else {
    days = Math.floor(diff / (1000 * 60 * 60 * 24));
    hours = Math.floor(diff / (1000 * 60 * 60) - days * 24);
    minutes = Math.floor(diff / (1000 * 60) - days * 24 * 60 - hours * 60);

    if (days !== 0) {
      expirationString += ` ${days} days`;
    }

    if ((days === 0 && hours !== 0) || days !== 0) {
      expirationString += ` ${hours} hours`;
    }

    if (days === 0) {
      expirationString += ` ${minutes} minutes`;
    }
  }

  return { expirationString, days, hours, minutes };
};

export const formatDate = (
  value: number,
  withYear?: boolean,
  withTime?: boolean
): string => {
  const d = new Date(value);

  return `${d.getDate()} ${months[d.getMonth()]} ${
    withYear ? d.getFullYear() : ''
  } ${withTime ? d.toLocaleTimeString() : ''}`;
};

// convert timestamp to year-month-day format
// value is string because timestamp is saved in DB as string
export const timestampToYYYYMMDD = (value: string): string => {
  return new Date(Number(value))
    .toISOString()
    .replace('Z', '')
    .replaceAll(/T.*$/g, '');
};

export const certificationColor = {
  APPROVED: 'green',
  EXPIRED: 'red',
  PENDING: 'purple',
  REJECTED: 'mediumGray',
  CLOSE_TO_EXPIRING: 'orange',
};

export const portToString = (port: PortType): string =>
  `${port.name} (${fromEnum(port.country)})`;

export const portToObject = (port: string): PortType => ({
  name: port.split(' (')[0],
  country: port.split('(')[1].replace(')', ''),
});

export const portValidation = (
  value: string,
  allPorts: PortType[]
): boolean => {
  const formattedPorts = allPorts.map((p) => portToString(p));

  return formattedPorts.includes(value);
};

export const getServiceRequestExpiration = (
  createdAt: string,
  duration: number
): number => parseInt(createdAt, 10) + duration * 60 * 60 * 1000;

export const isEmail = (value: string): boolean =>
  /^([a-zA-Z0-9_\-\\.]+)@([a-zA-Z0-9_\-\\.]+)\.([a-zA-Z]{2,7})$/.test(value);

export const isPassword = (value: string): boolean =>
  /^((?=[^A-Za-z]*[A-Za-z])[ -~]).{9,}$/.test(value);

export const isServiceUniqueId = (value: string): boolean =>
  /^[A-Z0-9]{2,3}[-]{1}[0-9]{2}[-]{1}[0-9]{1,9}$/.test(value);

export const isXDecimals = (
  value: string,
  numberOfDecimals: number
): boolean => {
  const result = new RegExp(`^\\d*(\\.\\d{0,${numberOfDecimals}})?$`).test(
    value
  );
  return result;
};

export const getFlag = (nationality: string): string =>
  countries.find((infoCountry) => infoCountry.name === nationality)?.CCA2;

// getValue is created to fix a bug in which some values are 'undefined' instead of undefined.
// The functions is used to return the actual value passed. If for any reason the value passed is 'undefined' or empty it returns undefined, othewise it returns back the value sent
export const getValue = (value: string | number): string | number =>
  value === 'undefined' || !value ? undefined : value;

// this is used by the getQueryValue function below
const urlRegex = (index: 0 | 1) =>
  typeof window !== 'undefined' &&
  window.location.pathname.match(/[a-f\d]{24}/g)[index];
// used to get a query value from router (e.g. /service-request/[ServiceRequestId]) with a fallback
// to avoid undefined on first render
export const getQueryValue = (
  // eslint-disable-next-line @typescript-eslint/no-shadow
  router: NextRouter,
  queryKey: string,
  queryKey2?: string
): string[] => {
  let result;

  if (queryKey2) {
    result = [
      router.query[queryKey] || urlRegex(0),
      router.query[queryKey2] || urlRegex(1),
    ];
  } else {
    result = [router.query[queryKey] || urlRegex(0)];
  }

  return result;
};

// checks if expiration date is less than a month away
export const checkExpirationDate = (expirationDate: string): boolean => {
  let result = false;
  const todayDate = new Date().getTime();

  if (
    parseInt(expirationDate, 10) - parseInt(todayDate.toString(), 10) <
    30 * 60 * 60 * 24 * 1000
  ) {
    result = true;
  }

  return result;
};

// makes a uuid for an equipment
export const equipmentUuid = (
  category: string,
  subcategory: string | undefined,
  title: string
): string => `${category}|${subcategory ? `${subcategory}|` : ''}${title}`;

export const ordinal = (number: number): string => {
  const englishOrdinalRules = new Intl.PluralRules('en', {
    type: 'ordinal',
  });
  const suffixes = {
    one: 'st',
    two: 'nd',
    few: 'rd',
    other: 'th',
  };
  const suffix = suffixes[englishOrdinalRules.select(number)];

  return number + suffix;
};

export const uniqueArrayValues = (array: string[]): string[] =>
  array.filter((v, i, a) => a.indexOf(v) === i);

export const hideExpirationDate = (
  serviceStatus: ServiceRequestStatusType,
  offerStatus: OfferStatusType
): boolean =>
  (!offerStatus &&
    ['OFFER_CONFIRMED', 'OFFER_FINALIZED', 'COMPLETED'].includes(
      serviceStatus
    )) ||
  offerStatus === 'DECLINED' ||
  serviceStatus === 'CANCELED' ||
  serviceStatus === 'OFFER_FINALIZED';

export const checkClassApproved = (
  cert: Partial<CertificationsType>
): boolean => cert.type === 'CLASS' && cert.status === 'APPROVED';

export const checkMakerApproved = (
  cert: Partial<CertificationsType>,
  makers: string[]
): boolean =>
  cert.type === 'MAKER' &&
  cert.status === 'APPROVED' &&
  makers?.includes(cert.title);

// checks if cert flag is approved and if it is the same with the ship's flag
export const checkFlagApproved = (
  cert: Partial<CertificationsType>,
  shipFlag: string
): boolean =>
  cert.type === 'FLAG' && cert.status === 'APPROVED' && cert.title === shipFlag;

export const clearUserData = (): void => {
  if (localStorage.getItem('wasAdmin')) {
    if (localStorage.getItem('companyServiceRequestsFilters')) {
      const serviceFiltersWithoutShipFilter = {
        ...JSON.parse(localStorage.getItem('companyServiceRequestsFilters')),
        serviceRequestsShipFilters: [],
      };
      localStorage.setItem(
        'companyServiceRequestsFilters',
        JSON.stringify(serviceFiltersWithoutShipFilter)
      );
    }
    if (localStorage.getItem('companyTroubleshootingRequestsFilters')) {
      const troubleshootingFiltersWithoutShipFilter = {
        ...JSON.parse(
          localStorage.getItem('companyTroubleshootingRequestsFilters')
        ),
        troubleshootingRequestsShipFilters: [],
      };
      localStorage.setItem(
        'companyTroubleshootingRequestsFilters',
        JSON.stringify(troubleshootingFiltersWithoutShipFilter)
      );
    }
    localStorage.removeItem('wasAdmin');
  }
  localStorage.removeItem('user');
  localStorage.removeItem('userId');
  localStorage.removeItem('session');
  localStorage.removeItem('sessionCreatedAt');
  localStorage.removeItem('userId');
  localStorage.removeItem('wasAdmin');
  localStorage.removeItem('brokerInfo');
  sessionStorage.removeItem('userBalance');
};

export interface getTransactionsProps {
  offset: number;
  limit: number;
  all: boolean;
  status?: TransactionStatusType;
  userId?: string;
  type?: TransactionTypeType;
}

export const getTransactions = async ({
  offset,
  limit,
  all = false,
  status,
  userId,
  type,
}: getTransactionsProps): Promise<Array<TransactionType>> => {
  const query = `{
    transactions(all: ${all}, limit: ${limit}, offset: ${offset}
      ${status ? `, status: ${status}` : ''}
      ${userId ? `, userId: "${userId}"` : ''}
      ${type ? `, type: ${type}` : ''}
  ) {
    _id
    type
    status
    createdAt
    price
    userId
    shipName
    serviceRequestUniqueId
    invoiceSentDate
    }
  }`;

  const response = await request(query);

  if (response.errors) {
    toast.error(response.errors[0].message);
    return null;
  }
  return response.data.transactions;
};

interface parseUrlReturn {
  hash: string;
  urlParams: Record<string, string>;
}

export const parseUrl = (): parseUrlReturn => {
  const value =
    typeof window !== 'undefined'
      ? window.location.hash || window.location.search
      : '';

  let hash = null;

  const urlParams = value?.includes('?')
    ? value
        .split('?')[1]
        .split('&')
        .reduce(
          (acc, cur) => ({
            ...acc,
            [cur.split('=')[0]]: cur.split('=')[1],
          }),
          {}
        )
    : null;

  if (value?.includes('#')) {
    if (value?.includes('?')) {
      // eslint-disable-next-line prefer-destructuring
      hash = value?.replace('#', '').split('?')[0];
    } else {
      hash = value?.replace('#', '');
    }
  }

  return {
    hash,
    urlParams,
  };
};

// change name to encodeText & decodetext
export const encodeText = (value: string): string => {
  return value?.replaceAll('"', '&quot;');
};

export const decodeText = (value: string): string => {
  return value && value.replaceAll('&quot;', '"');
};

// Format service request equipments into string based on if it is for query (e.g findSuppliers) or mutation (e.g upsert service request)
export const formatEquipments = (
  serviceRequest: ServiceRequestType,
  isFor: 'query' | 'mutation'
): string => {
  return serviceRequest.equipments.reduce(
    (acc, cur) =>
      `${acc}{
        title: ${toEnum(cur.title)},
        category: ${toEnum(cur.category)} ,
        ${cur.subcategory ? `subcategory: ${toEnum(cur.subcategory)},` : ''}
        ${
          isFor === 'query'
            ? `service: "${serviceRequest.standardService.title}",`
            : ''
        }
        ${cur.maker ? `maker: "${cur.maker}",` : ''}
        ${cur.type && isFor === 'mutation' ? `type: "${cur.type}",` : ''}
        ${
          cur.description && isFor === 'mutation'
            ? `description: """${encodeText(cur.description)}""",`
            : ''
        }
      }`,
    ''
  );
};

/**
 * Formats a number with 'precision' decimals
 * @param value the number to format
 * @param precision the amount of decimals
 * @returns the formatted number
 */
export const round = (value: number, precision: number): number => {
  const multiplier = 10 ** precision;
  return Math.round(value * multiplier) / multiplier;
};

// Dan Abramov useInterval
export const useInterval = (callback, delay: number | null): void => {
  const savedCallback = useRef();

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    const tick = () => {
      /*  eslint-disable-next-line @typescript-eslint/ban-ts-comment */
      /* @ts-ignore */
      savedCallback.current();
    };
    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
};

export const durationOptions = [
  { label: '6 Hours', value: '6' },
  { label: '12 Hours', value: '12' },
  { label: '24 Hours', value: '24' },
  { label: '2 Days', value: '48' },
  { label: '5 Days', value: '120' },
  { label: '7 Days', value: '168' },
  { label: '30 Days', value: '720' },
  { label: '90 Days', value: '2160' },
];

export const getEditOfferUrl = (
  serviceRequestId: string,
  myOfferId: string
): string => {
  return `/service-request/${serviceRequestId}/offer/${myOfferId}`;
};

export const isUnderwaterType = (
  equipment: string,
  standardService: string
): boolean => {
  return (
    equipment === 'UNDERWATER_HULL_AND_APPENDICES' &&
    (standardService === 'Hull Cleaning - Inspection/Propeller Polishing' ||
      standardService === 'Hull Cleaning/Propeller Polishing' ||
      standardService === 'IWS for Class purposes')
  );
};

// in phone mode the currency is converted to currency symbol
export const convertCurrencyUI = (
  currency: CurrencyType,
  isPhone: boolean
): CurrencyType | CurrencySymbolType => {
  let result;

  if (isPhone) {
    result = CurrencySymbolType[currency];
  } else {
    result = currency;
  }

  return result;
};

export const getUserShipsAsDropdownOptions = (
  user: UserType,
  requestType: 'Troubleshooting' | 'Service' | 'Exports'
): { label: string; value: string }[] => {
  const ships = user.ships
    ? user.ships
        .map((ship) => ({
          label: ship.name,
          value: ship.name,
        }))
        .sort((a, b) => {
          if (a.value > b.value) {
            return 1;
          }
          if (b.value > a.value) {
            return -1;
          }
          return 0;
        })
    : [{ label: 'You have no ships', value: 'no-ships' }];
  if (requestType === 'Service') {
    ships.push({
      label: 'Not owned ship',
      value: 'other',
    });
  }
  return ships;
};

const prePurchasingInspection = [
  {
    title: 'Regulatory Compliance and Commercial Inspections',
    items: [
      {
        title: 'Pre-Purchasing Surveys',
        description: 'dummy',
        items: [],
      },
    ],
  },
];

export const getShipEquipments = (
  user: UserType,
  selectedShip: ShipType['name'],
  requestType: 'troubleshooting' | 'service'
): TreeShipViewItemType[] => {
  let eq;
  if (!selectedShip) {
    eq = [];
  } else if (selectedShip === 'other') {
    eq = prePurchasingInspection;
  } else {
    eq = convertShipTreeEditToTreeView(
      convertEquipmentsToShipEditTree(
        user.ships?.find((s) => s.name === selectedShip)?.equipments ||
          ([{}] as ShipEquipmentType[])
      )
    )?.filter((category) => category.title !== 'Hull'); // hide the hull attachments structure that was added in the ship's equipment (when creating a service or troubleshooting request)

    // add extra the Regulatory inspections (if it's a serviceRequest)
    // we desctructure this to lose reference to equipmentStructure, because we will modify it just below (remove pre-purchasing)
    if (requestType === 'service') {
      const regulatory = {
        ...equipmentStructure.find(
          (e) => e.title === 'Regulatory Compliance and Commercial Inspections'
        ),
      };

      // remove Pre-purchasing inspections and add dummy maker to force checkboxes to appear
      regulatory.items = regulatory.items
        .filter((item) => item.title !== 'Pre-Purchasing Surveys')
        .map((item) => ({
          ...item,
          maker: ' ',
        }));

      eq.push(regulatory);
    }
  }

  // if we have ship equipments (but its not only Regulatory, which means we selected not owned ship)
  // also add hull and Bridge Equipment -> Radio Survey Items
  if (
    eq.length &&
    eq[0].title !== 'Regulatory Compliance and Commercial Inspections'
  ) {
    // hull
    const hull = {
      title: equipmentStructure[0].title,
      items: equipmentStructure[0].items.map((i) => ({
        title: i.title,
        maker: ' ',
        items: i.items.map((k) => ({
          title: k.title,
        })),
      })),
    };
    eq.unshift(hull);

    // and for radio survey items
    const radioSurveyItems = {
      title: 'Radio Survey Items',
      maker: '',
      type: '',
      description: '',
      items: [],
    };
    const bridgeEquipment = {
      title: 'Bridge Equipment',
      items: [radioSurveyItems],
    };
    // check if bridge equipment already exists
    if (eq.find((category) => category.title === 'Bridge Equipment')) {
      const bridgeIndex = eq.findIndex(
        (category) => category.title === 'Bridge Equipment'
      );
      eq[bridgeIndex].items.push(radioSurveyItems);
      // and now order the bridge items alphabetically
      eq[bridgeIndex].items = eq[bridgeIndex].items.sort((a, b) =>
        a.title.toLowerCase().localeCompare(b.title.toLowerCase())
      );
    } else {
      // if regulatory exists, add bridge equipment pre-last
      // eslint-disable-next-line no-lonely-if
      if (
        eq[eq.length - 1].title ===
        'Regulatory Compliance and Commercial Inspections'
      ) {
        eq.splice(-1, 0, bridgeEquipment);
      } else {
        eq.push(bridgeEquipment);
      }
    }
  }

  return eq;
};
export const isCommissionActive = (
  user: UserType,
  offer?: OfferLumpsumType | OfferManHoursType | OfferUnderwaterType
): boolean => {
  let result = user.type === 'SUPPLIER' && Boolean(offer?.commission);

  if (user.bundlesEnabled) {
    result = user.remainingServices === 0 && !user.fees?.nobundle;
  }

  return result;
};

// calculate the cost after commission
export const getCostWithCommission = (
  cost: number,
  user: UserType,
  offer?: OfferLumpsumType | OfferManHoursType | OfferUnderwaterType,
  isForEditOffer = false
): number => {
  if (
    (isCommissionActive(user, offer) && isForEditOffer) ||
    (user.type === 'SHIPPING_COMPANY' &&
      offer.user.remainingServices === 0 &&
      !offer.user.fees?.nobundle &&
      offer.commission)
  ) {
    return round(cost + cost * (offer.commission / 100), 2);
  }
  return cost;
};

// calculate the commission out of a total cost
export const getCommissionCost = (
  totalCost: number,
  commission?: number
): number =>
  commission ? round(totalCost - (totalCost / (100 + commission)) * 100, 2) : 0;

export const showDashboardInfoBox = (
  isLoading: boolean,
  requestsNumber: number,
  hideServiceRequestsShowMoreButton: boolean
): boolean =>
  !isLoading && requestsNumber === 0 && hideServiceRequestsShowMoreButton;

export const getDashboardInfoBoxMessage = (
  user: UserType,
  areFiltersActive: boolean,
  requestType: 'service' | 'troubleshooting' = 'service'
): string => {
  let result = 'There are no requests available.';
  if (user.type === 'SHIPPING_COMPANY') {
    if (areFiltersActive && user.ships?.length > 0) {
      result = `There are no ${requestType} requests with the filters you have selected.`;
    } else {
      result = `The ${requestType} requests you will create are going to appear here.`;
    }
  } else if (user.type === 'SUPPLIER') {
    if (areFiltersActive) {
      result = `There are no ${requestType} requests with the filters you have selected.`;
    } else {
      result = `${toTitleCase(
        requestType
      )} requests will appear here. We will also inform you with an email.`;
    }
  }

  return result;
};

// calculate the money a user owes to bid2board based on fixed value transactions
export const getRemainingBalance = (transactions: TransactionType[]): number =>
  round(
    transactions
      .filter(
        (t) =>
          supplierBalanceTransactionStatuses.includes(t.status) &&
          (t.type === 'FIXED' || t.type === 'YEARLY')
      )
      .reduce((acc, cur) => acc + cur.price, 0),
    2
  );

export const hasNewMessage = (
  userType: UserTypeType,
  offer:
    | OfferLumpsumType
    | OfferManHoursType
    | OfferUnderwaterType
    | TroubleshootingOfferType
): boolean =>
  userType === UserTypeType.SHIPPING_COMPANY
    ? offer.newSupplierMessage
    : offer.newCompanyMessage;

// timeUnit is enum so convert it to string for using it
export const formattedTimeUnit = (
  offerTimeUnit: TimeUnitType | undefined
): string => (offerTimeUnit ? fromEnum(offerTimeUnit) : 'hours');

export const getTransactionTotalPrice = (
  transaction: TransactionType
): number => {
  let totalPrice: number;

  if (
    transaction.type === 'FIXED' ||
    transaction.type === 'NOBUNDLE' ||
    transaction.type === 'YEARLY'
  ) {
    totalPrice = transaction.price;
  } else {
    totalPrice =
      transaction.price * parseFloat(transaction.type.replace('BUNDLE', ''));
  }

  return round(totalPrice, 2);
};

export const formattedInitialDataDate = (initialDate: string): string => {
  let result;
  if (initialDate) {
    if (initialDate.includes('-')) {
      result = initialDate;
    } else {
      result = timestampToYYYYMMDD(initialDate);
    }
  } else {
    result = '';
  }
  return result;
};

export const calculateDiscount = (oldCost: number, newCost: number): number =>
  round(100 * ((oldCost - newCost) / oldCost), 2);

// upload single document
export const uploadSingleDocument = async (file: File, userId: string) => {
  try {
    const response = await upload(file, userId);
    return response.data.upload.url;
  } catch (error) {
    toast.error(error);
    return 'error';
  }
};

export const clearStringArrayOfMatchingElements = (array: string[]) => {
  let cleanArray = [...array];
  let cleanArrayIndex = 0;

  while (cleanArray.length > cleanArrayIndex) {
    const itemToCheck = cleanArray[cleanArrayIndex];
    const matchingIndex = cleanArray.findIndex(
      (item) => fuzzy(item, itemToCheck) > 0.8
    );
    cleanArray = [
      ...cleanArray.filter((item, index) =>
        fuzzy(item, itemToCheck) > 0.8 ? index === matchingIndex : true
      ),
    ];
    cleanArrayIndex += 1;
  }

  return cleanArray;
};

export const EDIT_OFFER_LIMIT = 4;
